import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Award, Users, MessageSquare, ShoppingBag, ChevronLeft } from 'lucide-react';
import FeatureCard from '../components/ui/FeatureCard';
import TestimonialCard from '../components/ui/TestimonialCard';
import banner2 from '../assets/banner2.png';
import banner3 from '../assets/banner3.png';
import banner4 from '../assets/banner4.png';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const banners = [
    {
      image: banner2,
      title: 'Múltiples Disciplinas',
      subtitle: 'De voleibol a ciclismo, todas las atletas tienen su lugar aquí',
    },
    {
      image: banner3,
      title: 'Competencia de Alto Nivel',
      subtitle: 'Atletismo, natación y gimnasia: alcanza tus metas',
    },
    {
      image: banner4,
      title: 'Tu Pasión, Tu Deporte',
      subtitle: 'Fútbol, baloncesto y tenis: encuentra tu comunidad',
    },
  ];

  useEffect(() => {
    document.title = 'FemSportNet - Empowering Female Athletes';

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Users className="h-6 w-6 text-green-500" />,
      title: 'Conecta con Atletas',
      description: 'Construye tu red con atletas femeninas afines de todo el mundo.',
    },
    {
      icon: <MessageSquare className="h-6 w-6 text-green-500" />,
      title: 'Mensajería Segura',
      description: 'Comunícate de forma privada con mensajería cifrada de extremo a extremo.',
    },
    {
      icon: <Award className="h-6 w-6 text-green-500" />,
      title: 'Muestra tus Logros',
      description: 'Exhibe tus logros deportivos, estadísticas y momentos destacados de tu carrera.',
    },
    {
      icon: <ShoppingBag className="h-6 w-6 text-green-500" />,
      title: 'Marketplace Deportivo',
      description: 'Descubre productos y servicios diseñados especialmente para atletas femeninas.',
    },
  ];

  const testimonials = [
    {
      name: 'Sophia Williams',
      role: 'Tenista Profesional',
      image: 'https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      quote: 'FemSportNet ha sido fundamental para ayudarme a conectar con otras atletas y encontrar nuevas oportunidades en mi deporte.',
    },
    {
      name: 'Olivia Martinez',
      role: 'Nadadora Olímpica',
      image: 'https://images.pexels.com/photos/3764574/pexels-photo-3764574.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      quote: 'He encontrado oportunidades de patrocinio y hecho conexiones valiosas a través de esta plataforma que no habría encontrado en otro lugar.',
    },
    {
      name: 'Emma Johnson',
      role: 'Entrenadora de Baloncesto',
      image: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      quote: 'Como entrenadora, FemSportNet me ayuda a descubrir atletas talentosas y mantenerme conectada con mi equipo en una plataforma centralizada.',
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-200">
      {/* Hero Section - Carousel */}
      <section className="relative">
        <div className="h-[600px] overflow-hidden relative">
          {banners.map((banner, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent"></div>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-center z-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {banners[currentSlide].title}
              </h1>
              <p className="text-lg md:text-xl text-gray-200 mb-8">
                {banners[currentSlide].subtitle}
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link to="/register" className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-3 rounded-md inline-flex items-center justify-center transition-colors duration-200">
                  Únete Ahora
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/about" className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-medium px-6 py-3 rounded-md inline-flex items-center justify-center transition-all duration-200">
                  Conoce Más
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-colors duration-200"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-colors duration-200"
          aria-label="Siguiente"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-green-500 w-8'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Ir a slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-green-500">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white">15K+</p>
              <p className="text-white text-opacity-90 mt-2">Atletas</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white">50+</p>
              <p className="text-white text-opacity-90 mt-2">Deportes</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white">100+</p>
              <p className="text-white text-opacity-90 mt-2">Países</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-white">5K+</p>
              <p className="text-white text-opacity-90 mt-2">Oportunidades</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-200">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Diseñado para Atletas Femeninas
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Nuestra plataforma ofrece características únicas diseñadas para apoyar tu viaje atlético y conectarte con una comunidad global de deportistas.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 md:py-24 bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Historias de Éxito
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Escucha de atletas que han avanzado en sus carreras y encontrado oportunidades a través de FemSportNet.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index}
                name={testimonial.name}
                role={testimonial.role}
                image={testimonial.image}
                quote={testimonial.quote}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">¿Lista para Unirte a la Comunidad?</h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Crea tu perfil hoy y conecta con miles de atletas femeninas de todo el mundo. Muestra tus logros, encuentra oportunidades y haz crecer tu red.
            </p>
            <div className="mb-6">
              <p className="text-green-100 mb-2">¿Tienes preguntas? Contáctanos:</p>
              <a 
                href="mailto:femsportnet@femsportnet.com" 
                className="text-green-300 hover:text-green-200 font-medium underline transition-colors duration-200"
              >
                femsportnet@femsportnet.com
              </a>
            </div>
            <Link to="/register" className="bg-green-500 hover:bg-green-600 text-white font-medium px-8 py-4 rounded-md inline-flex items-center justify-center transition-colors duration-200">
              Comienza Ahora
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;